<script setup>
import Modal from "@Components/Modal/Modal.vue";
import { inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import Tooltip from "@Components/Tooltip.vue";
import { ArrowDownTrayIcon, CheckIcon, TrashIcon } from "@heroicons/vue/20/solid";
import { TrashIcon as TrashIconOutline } from "@heroicons/vue/24/outline";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { useForm } from "@/form";
import { PrimaryButton } from "@Components/Button";
import { useStore } from "@/Stores/templates";
import LoadingAnimation from "@Components/Animation/Loading.vue";
import ImportTemplateModal from "./ImportTemplateModal.vue";

const emit = defineEmits(["deleted"]);

const { t: __ } = useI18n();
const store = useStore();
const $notify = inject("$notify");

const modal = ref();
const destroyModalOpen = ref(false);
const importModal = ref();

const exportForm = useForm({
    template: undefined,
});

const destroyForm = useForm({
    template: undefined,
});

const show = () => {
    modal.value.show();
};

const hide = () => {
    modal.value.hide();
};

const exportTemplate = async (template) => {
    if (exportForm.processing) {
        return;
    }

    exportForm.template = template.id;

    try {
        const response = await exportForm.post("templates_export");
        await navigator.clipboard.writeText(response);
    } catch (err) {}
};

const showDestroyTemplateModal = (template) => {
    destroyForm.template = template.id;
    destroyModalOpen.value = true;
};

const destroyTemplate = () => {
    if (destroyForm.processing) {
        return;
    }

    destroyForm
        .post("templates_destroy")
        .then(() => {
            $notify({
                title: __("Le template de contenu #{id} a été supprimé.", { id: destroyForm.template }),
                type: "success",
            });

            store.fetch();

            destroyForm.reset();

            destroyModalOpen.value = false;
        })
        .catch(() => {
            destroyModalOpen.value = false;

            $notify({
                title: __("Une erreur s'est produite lors de la suppression."),
                type: "error",
            });

            destroyForm.reset();
        });
};

defineExpose({ show, hide });
</script>

<template>
    <Modal ref="modal" class="overflow-hidden sm:max-w-2xl">
        <div class="max-h-[80vh] flex flex-col">
            <div class="flex flex-none justify-between items-center px-4 py-4 space-x-3 border-b border-gray-300 dark:border-gray-800 lg:px-8 sm:px-6">
                <DialogTitle class="text-lg font-semibold leading-6 text-gray-900 dark:text-white">{{ __("Templates") }}</DialogTitle>
                <PrimaryButton @click="importModal.show()" :z="1">
                    {{ __("Importer un template") }}
                </PrimaryButton>
            </div>

            <div class="min-h-0 flex flex-col flex-1">
                <div class="overflow-y-auto flex-1">
                    <div v-if="!store.isLoadedData" class="flex justify-center items-center px-4 py-4 lg:px-8 sm:px-6">
                        <LoadingAnimation class="w-20 h-20" />
                    </div>
                    <table v-else-if="store.templates?.length > 0" class="w-full text-left whitespace-nowrap">
                        <thead class="sticky top-0 bg-gray-100 dark:bg-gray-800">
                            <tr>
                                <th class="py-4 pr-8 pl-4 text-sm text-gray-900 dark:text-white lg:pl-8 sm:pl-6">
                                    {{ __("Nom") }}
                                </th>
                                <th class="py-4 pr-8 pl-0 text-sm text-gray-900 dark:text-white">
                                    {{ __("Mode") }}
                                </th>
                                <th class="py-4 pr-4 pl-0 text-right lg:pr-8 sm:pr-6"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-300 dark:divide-gray-800">
                            <tr v-for="template in store.templates" :key="template.id">
                                <td class="w-full py-4 pr-8 pl-4 lg:pl-8 sm:pl-6">
                                    <div class="flex flex-col gap-y-1">
                                        <div class="text-sm font-medium text-gray-900 truncate dark:text-white">{{ template.name }}</div>
                                        <div class="mt-0.5 text-xs italic text-gray-600 dark:text-gray-400">v{{ template.plugin_version }}</div>
                                    </div>
                                </td>
                                <td class="py-4 pr-8 pl-0 text-sm text-gray-600 dark:text-gray-400">
                                    {{ template.payload.modules.content.automatic.enabled ? __("Classique") : __("Avancé") }}
                                </td>
                                <td class="py-4 pr-4 pl-0 text-right lg:pr-8 sm:pr-6">
                                    <div class="flex justify-end items-center space-x-3">
                                        <Tooltip :content="__('Copier le code du template')">
                                            <button
                                                @click="exportTemplate(template)"
                                                class="flex items-center text-gray-600 rounded-full dark:text-gray-400 dark:focus-visible:outline-white dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                                                <svg
                                                    v-if="exportForm.processing && exportForm.template === template.id"
                                                    class="w-5 h-5 animate-spin"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                    <path
                                                        class="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <CheckIcon
                                                    v-else-if="exportForm.recentlySuccessful && exportForm.template === template.id"
                                                    class="w-5 h-5"
                                                    aria-hidden="true" />
                                                <ArrowDownTrayIcon v-else class="w-5 h-5" aria-hidden="true" />
                                            </button>
                                        </Tooltip>

                                        <Tooltip :content="__('Supprimer')">
                                            <button
                                                @click="showDestroyTemplateModal(template)"
                                                class="flex items-center text-red-500 rounded-full focus-visible:outline-2 focus-visible:outline-red-500 focus-visible:outline focus-visible:outline-offset-2 hover:text-red-400">
                                                <TrashIcon class="w-5 h-5" aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="flex justify-center items-center px-4 py-4 lg:px-8 sm:px-6">
                        <div class="text-sm text-gray-600 dark:text-gray-400">{{ __("Aucun template trouvé.") }}</div>
                    </div>
                </div>
            </div>
        </div>

        <TransitionRoot as="template" :show="destroyModalOpen">
            <Dialog as="div" class="z-[100000] relative" @close="destroyModalOpen = false">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="bg-opacity-75 fixed inset-0 bg-gray-900 transition-opacity" />
                </TransitionChild>

                <div class="z-[100000] overflow-y-auto fixed inset-0">
                    <div class="min-h-full flex justify-center items-end p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-300"
                            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leave-from="opacity-100 translate-y-0 sm:scale-100"
                            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel
                                class="transform overflow-hidden relative px-4 pt-5 pb-4 text-left bg-gray-800 rounded-lg border border-gray-700 shadow-xl transition-all sm:w-full sm:max-w-lg sm:p-6 sm:my-8">
                                <div class="sm:flex sm:items-start">
                                    <div
                                        class="flex-shrink-0 mx-auto w-12 h-12 flex justify-center items-center bg-red-500 rounded-full sm:w-10 sm:h-10 sm:mx-0">
                                        <TrashIconOutline class="w-6 h-6 text-gray-200" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-white">
                                            {{ __("Supprimer le template de contenu #{id}", { id: destroyForm.template }) }}
                                        </DialogTitle>
                                        <div class="mt-2">
                                            <p class="text-sm text-gray-400">
                                                {{
                                                    __(
                                                        "Êtes-vous sûr de vouloir supprimer le template de contenu #{id} ? Cette action ne peut pas être annulée.",
                                                        {
                                                            id: destroyForm.template,
                                                        }
                                                    )
                                                }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:flex sm:flex-row-reverse sm:mt-4">
                                    <button
                                        type="button"
                                        class="w-full inline-flex justify-center items-center px-3 py-2 text-sm font-semibold text-white bg-red-500 rounded-md shadow-sm focus-visible:outline-2 focus-visible:outline-red-500 focus-visible:outline focus-visible:outline-offset-2 hover:bg-red-400 sm:w-auto sm:ml-3"
                                        @click="destroyTemplate">
                                        {{ __("Supprimer") }}
                                    </button>
                                    <button
                                        type="button"
                                        class="w-full inline-flex justify-center items-center px-3 py-2 mt-3 text-sm font-semibold text-gray-400 bg-gray-800 rounded-md focus-visible:outline-2 focus-visible:outline-gray-700 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-200 hover:bg-gray-700 hover:shadow-sm sm:w-auto sm:mt-0"
                                        @click="destroyModalOpen = false">
                                        {{ __("Annuler") }}
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

        <ImportTemplateModal ref="importModal" />
    </Modal>
</template>
